/**
 * PlanningSetupPage - Create or edit a planning scenario.
 *
 * Routes:
 *   /planning/new        - Create new plan
 *   /planning/:id/edit   - Edit existing plan
 *
 * Multi-step wizard:
 *   Step 1: Basic info (name, description, objective)
 *   Step 2: Select community to apply
 *   Step 3: Configure what-if parameters (asset changes, tariff changes)
 *   Step 4: Choose analysis type (CBA or Long-term Energy Benefits)
 *
 * Creates or updates a scenario via the scenario-service API.
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Container, Row, Col, Card, Form, Button, Badge, Alert, Spinner,
} from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useCommunities, useCommunity } from '../hooks/useCommunities'
import {
  useScenario,
  useCreateScenario,
  useUpdateScenario,
} from '../hooks/useScenarios'
import CBAParamsForm from '../components/CBAParamsForm'
import LongTermParamsForm from '../components/LongTermParamsForm'
import OptimizationParamsForm from '../components/OptimizationParamsForm'
import { useCommunityAnnualLoad } from '../hooks/useDatasets'

function PlanningSetupPage() {
  const { id: scenarioId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isEditing = !!scenarioId

  // Current wizard step (1-4)
  const [step, setStep] = useState(1)

  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',
    objective: 'min_cost',
    horizon_days: 1,
    // Step 2: Community
    community_id: '',
    // Step 3: What-if Parameters (type-specific)
    whatif: {
      pv_capacity_change_pct: 0,
      battery_capacity_change_pct: 0,
      tariff_change_pct: 0,
      load_change_pct: 0,
      add_community_battery: false,
      new_battery_kwh: 100,
    },
    cbaParams: null, // Populated when analysis_type = 'cba'
    longTermParams: null, // Populated when analysis_type = 'long_term'
    optimizationParams: null, // Populated when analysis_type = 'optimization'
    // Analysis Type (moved to Step 1)
    analysis_type: 'optimization', // optimization | cba | long_term
  })

  // API hooks
  const { data: communities = [] } = useCommunities()
  const { data: existingScenario, isLoading: loadingScenario } = useScenario(scenarioId)
  const { data: selectedCommunity } = useCommunity(formData.community_id || null)
  const { data: computedAnnualLoad } = useCommunityAnnualLoad(formData.community_id || null)
  const createScenario = useCreateScenario()
  const updateScenario = useUpdateScenario(scenarioId)

  // Populate form when editing
  useEffect(() => {
    if (existingScenario) {
      const meta = existingScenario.metadata || {}
      setFormData((prev) => ({
        ...prev,
        name: existingScenario.name || '',
        description: meta.description || '',
        objective: existingScenario.objective || 'min_cost',
        horizon_days: existingScenario.horizon_days || 1,
        community_id: existingScenario.community_id || '',
        whatif: meta.whatif || prev.whatif,
        analysis_type: meta.analysis_type || 'optimization',
        // Restore analysis-type-specific params so fork/edit preserves full configuration
        ...(meta.cbaParams ? { cbaParams: meta.cbaParams } : {}),
        ...(meta.longTermParams ? { longTermParams: meta.longTermParams } : {}),
        ...(meta.optimizationParams ? { optimizationParams: meta.optimizationParams } : {}),
      }))
    }
  }, [existingScenario])

  /** Update a form field. */
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  /** Update a what-if parameter. */
  const updateWhatIf = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      whatif: { ...prev.whatif, [field]: value },
    }))
  }

  /** Validate current step before proceeding. */
  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.name.trim().length > 0
      case 2:
        return formData.community_id.length > 0
      case 3:
        return true // what-if params are optional
      case 4:
        return formData.analysis_type.length > 0
      default:
        return false
    }
  }

  /** Submit the form (create or update scenario). */
  const handleSubmit = async () => {
    try {
      // Build scenario payload for the backend
      // Store analysis-type-specific params in metadata so backend can read them
      const typeSpecificMeta = {}
      if (formData.analysis_type === 'optimization' && formData.optimizationParams) {
        typeSpecificMeta.optimizationParams = formData.optimizationParams
        // Use the objective from the optimization form if available
        typeSpecificMeta.objective = formData.optimizationParams.objective
      } else if (formData.analysis_type === 'cba' && formData.cbaParams) {
        typeSpecificMeta.cbaParams = formData.cbaParams
      } else if (formData.analysis_type === 'long_term' && formData.longTermParams) {
        typeSpecificMeta.longTermParams = formData.longTermParams
      }

      // Only include objective/horizon for optimization scenarios
      const isOptimization = formData.analysis_type === 'optimization'
      const scenarioPayload = {
        name: formData.name,
        community_id: formData.community_id,
        objective: isOptimization ? (formData.optimizationParams?.objective || formData.objective) : null,
        horizon_days: isOptimization ? formData.horizon_days : null,
        status: 'draft',
        metadata: {
          description: formData.description,
          analysis_type: formData.analysis_type,
          whatif: formData.whatif,
          ...typeSpecificMeta,
        },
      }

      let savedScenario
      if (isEditing) {
        savedScenario = await updateScenario.mutateAsync(scenarioPayload)
      } else {
        savedScenario = await createScenario.mutateAsync(scenarioPayload)
      }

      const savedId = savedScenario?.id || scenarioId

      // Navigate to results page, passing full form data via router state
      // so the results page can use it even before the scenario is fully persisted
      navigate(`/planning/${savedId}/results?type=${formData.analysis_type}`, {
        state: {
          formData,
          cbaParams: formData.cbaParams,
          longTermParams: formData.longTermParams,
          optimizationParams: formData.optimizationParams,
          communityId: formData.community_id,
          currency: selectedCommunity?.currency || 'EUR',
        },
      })
    } catch (error) {
      // If API save fails, still navigate with params via state so results page works
      console.warn('Scenario save failed, navigating with local params:', error.message)
      const fallbackId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`
      navigate(`/planning/${fallbackId}/results?type=${formData.analysis_type}`, {
        state: {
          formData,
          cbaParams: formData.cbaParams,
          longTermParams: formData.longTermParams,
          optimizationParams: formData.optimizationParams,
          communityId: formData.community_id,
          currency: selectedCommunity?.currency || 'EUR',
        },
      })
    }
  }

  /** Wizard step definitions. */
  const steps = [
    { number: 1, label: t('planning_setup.step1_label') },
    { number: 2, label: t('planning_setup.step2_label') },
    { number: 3, label: t('planning_setup.step3_label') },
    { number: 4, label: t('planning_setup.step4_label') },
  ]

  // Loading state for editing
  if (isEditing && loadingScenario) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
        <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
          {t('planning_setup.loading_scenario')}
        </p>
      </Container>
    )
  }

  return (
    <Container style={{ maxWidth: '800px' }}>
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3 mb-3">
            <Link to="/planning" style={{ color: 'var(--color-neutral-600)', textDecoration: 'none', fontSize: 'var(--font-size-sm)' }}>
              {t('planning_setup.back_link')}
            </Link>
          </div>
          <h1 style={{ color: 'var(--color-primary-dark)', fontSize: 'var(--font-size-2xl)' }}>
            {isEditing ? t('planning_setup.header_edit') : t('planning_setup.header_new')}
          </h1>
          <p style={{ color: 'var(--color-neutral-600)' }}>
            {isEditing
              ? t('planning_setup.subheader_edit')
              : t('planning_setup.subheader_new')
            }
          </p>
        </Col>
      </Row>

      {/* Wizard Steps Indicator */}
      <div className="wizard-steps">
        {steps.map((s, index) => (
          <React.Fragment key={s.number}>
            <div className={`wizard-step ${step === s.number ? 'active' : ''} ${step > s.number ? 'completed' : ''}`}>
              <div className="wizard-step-number">
                {step > s.number ? '\u2713' : s.number}
              </div>
              <span className="wizard-step-label">{s.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`wizard-step-connector ${step > s.number ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card className="shadow-sm" style={{ borderRadius: 'var(--radius-lg)', border: 'none' }}>
        <Card.Body style={{ padding: 'var(--space-8)' }}>

          {/* Step 1: Scenario Info (includes Analysis Type selection) */}
          {step === 1 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-6)' }}>
                {t('planning_setup.step1_title')}
              </h4>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>{t('planning_setup.field_name')}</Form.Label>
                <Form.Control
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder={t('planning_setup.field_name_placeholder')}
                  maxLength={255}
                />
                <Form.Text className="text-muted">
                  {t('planning_setup.field_name_help')}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>{t('planning_setup.field_description')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder={t('planning_setup.field_description_placeholder')}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>{t('planning_setup.field_analysis_type')}</Form.Label>
                <Row className="g-3 mt-2">
                  <Col xs={12}>
                    <Card
                      onClick={() => updateField('analysis_type', 'optimization')}
                      style={{
                        cursor: 'pointer',
                        border: formData.analysis_type === 'optimization'
                          ? '2px solid var(--color-primary)'
                          : '1px solid var(--color-neutral-200)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: formData.analysis_type === 'optimization' ? 'var(--color-primary-pale)' : 'white',
                      }}
                    >
                      <Card.Body className="py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div style={{ fontSize: '1.5rem' }}>&#x26A1;</div>
                          <div>
                            <h6 style={{ marginBottom: '0.25rem', fontWeight: 600 }}>{t('planning_setup.type_optimization')}</h6>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                              {t('planning_setup.type_optimization_desc')}
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12}>
                    <Card
                      onClick={() => updateField('analysis_type', 'cba')}
                      style={{
                        cursor: 'pointer',
                        border: formData.analysis_type === 'cba'
                          ? '2px solid var(--color-primary)'
                          : '1px solid var(--color-neutral-200)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: formData.analysis_type === 'cba' ? 'var(--color-primary-pale)' : 'white',
                      }}
                    >
                      <Card.Body className="py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div style={{ fontSize: '1.5rem' }}>&#x1F4B0;</div>
                          <div>
                            <h6 style={{ marginBottom: '0.25rem', fontWeight: 600 }}>
                              {t('planning_setup.type_cba')}
                            </h6>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                              {t('planning_setup.type_cba_desc')}
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12}>
                    <Card
                      onClick={() => updateField('analysis_type', 'long_term')}
                      style={{
                        cursor: 'pointer',
                        border: formData.analysis_type === 'long_term'
                          ? '2px solid var(--color-primary)'
                          : '1px solid var(--color-neutral-200)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: formData.analysis_type === 'long_term' ? 'var(--color-primary-pale)' : 'white',
                      }}
                    >
                      <Card.Body className="py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div style={{ fontSize: '1.5rem' }}>&#x1F4C5;</div>
                          <div>
                            <h6 style={{ marginBottom: '0.25rem', fontWeight: 600 }}>
                              {t('planning_setup.type_long_term')}
                            </h6>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                              {t('planning_setup.type_long_term_desc')}
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Form.Group>
            </div>
          )}

          {/* Step 2: Select Community */}
          {step === 2 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-6)' }}>
                {t('planning_setup.step2_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-4)' }}>
                {t('planning_setup.step2_text')}
              </p>

              {communities.length === 0 ? (
                <Alert variant="warning">
                  {t('planning_setup.no_communities')}{' '}
                  <Link to="/communities/new">{t('planning_setup.no_communities_link')}</Link>{' '}
                  {t('planning_setup.no_communities_suffix')}
                </Alert>
              ) : (
                <Row className="g-3">
                  {communities.map((c) => {
                    const isSelected = formData.community_id === c.id
                    const cStatus = c.metadata?.status || 'setup'
                    const buildingCount = c.buildings?.length || 0

                    return (
                      <Col key={c.id} xs={12} md={6}>
                        <Card
                          onClick={() => updateField('community_id', c.id)}
                          className="h-100"
                          style={{
                            cursor: 'pointer',
                            border: isSelected
                              ? '2px solid var(--color-primary)'
                              : '1px solid var(--color-neutral-200)',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: isSelected ? 'var(--color-primary-pale)' : 'white',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Card.Body className="py-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <h6 style={{ margin: 0, fontWeight: 600 }}>{c.name}</h6>
                              {isSelected && (
                                <Badge bg="none" style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}>
                                  {t('planning_setup.badge_selected')}
                                </Badge>
                              )}
                            </div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                              <span>{c.metadata?.country || t('common.n_a')}</span>
                              {' | '}
                              <span>{cStatus === 'active' ? t('planning_setup.status_active') : t('planning_setup.status_setup')}</span>
                              {' | '}
                              <span>{c.timezone}</span>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>
              )}

              {/* Selected Community Preview */}
              {selectedCommunity && (
                <Card className="mt-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-md)' }}>
                  <Card.Body>
                    <h6 style={{ fontWeight: 600, color: 'var(--color-primary-dark)' }}>
                      {t('planning_setup.summary_title', { name: selectedCommunity.name })}
                    </h6>
                    <Row className="g-2 mt-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                      <Col xs={4}>
                        <div style={{ color: 'var(--color-neutral-600)' }}>{t('planning_setup.buildings_label')}</div>
                        <div style={{ fontWeight: 600 }}>
                          {selectedCommunity.buildings?.length || 0}
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div style={{ color: 'var(--color-neutral-600)' }}>{t('planning_setup.assets_label')}</div>
                        <div style={{ fontWeight: 600 }}>
                          {selectedCommunity.community_assets?.length || 0}
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div style={{ color: 'var(--color-neutral-600)' }}>{t('planning_setup.datasets_label')}</div>
                        <div style={{ fontWeight: 600 }}>
                          {selectedCommunity.community_datasets?.length || 0}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}

          {/* Step 3: What-If Parameters (fully conditional on analysis type) */}
          {step === 3 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)' }}>
                {t('planning_setup.step3_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-6)' }}>
                {formData.analysis_type === 'optimization' && t('planning_setup.step3_opt_text')}
                {formData.analysis_type === 'cba' && t('planning_setup.step3_cba_text')}
                {formData.analysis_type === 'long_term' && t('planning_setup.step3_lt_text')}
              </p>

              {/* CBA Parameters Form */}
              {formData.analysis_type === 'cba' && (
                <CBAParamsForm
                  params={formData.cbaParams}
                  onChange={(params) => setFormData(prev => ({ ...prev, cbaParams: params }))}
                  community={selectedCommunity}
                  computedAnnualLoad={computedAnnualLoad}
                />
              )}

              {/* Long-Term Parameters Form */}
              {formData.analysis_type === 'long_term' && (
                <LongTermParamsForm
                  params={formData.longTermParams}
                  onChange={(params) => setFormData(prev => ({ ...prev, longTermParams: params }))}
                />
              )}

              {/* Day-Ahead Optimization Parameters */}
              {formData.analysis_type === 'optimization' && (
                <OptimizationParamsForm
                  params={formData.optimizationParams}
                  onChange={(params) => setFormData(prev => ({ ...prev, optimizationParams: params }))}
                />
              )}
            </div>
          )}

          {/* Step 4: Review & Confirm */}
          {step === 4 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-6)' }}>
                {t('planning_setup.step4_title')}
              </h4>

              <Card className="mb-4" style={{
                border: '1px solid var(--color-primary-light)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-primary-pale)',
              }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, color: 'var(--color-primary-dark)', marginBottom: 'var(--space-3)' }}>
                    {t('planning_setup.summary_card_title')}
                  </h6>
                  <Row style={{ fontSize: 'var(--font-size-sm)' }}>
                    <Col sm={6}>
                      <div className="mb-2"><strong>{t('planning_setup.summary_name')}</strong> {formData.name || '--'}</div>
                      <div className="mb-2"><strong>{t('planning_setup.summary_description')}</strong> {formData.description || t('planning_setup.summary_description_none')}</div>
                      <div className="mb-2"><strong>{t('planning_setup.summary_community')}</strong> {communities.find(c => c.id === formData.community_id)?.name || '--'}</div>
                    </Col>
                    <Col sm={6}>
                      <div className="mb-2"><strong>{t('planning_setup.summary_analysis_type')}</strong> {
                        formData.analysis_type === 'optimization' ? t('planning_setup.type_optimization') :
                          formData.analysis_type === 'cba' ? t('planning_setup.type_cba') :
                            t('planning_setup.type_long_term')
                      }</div>
                      {selectedCommunity && (
                        <>
                          <div className="mb-2"><strong>{t('planning_setup.summary_buildings')}</strong> {selectedCommunity.buildings?.length || 0}</div>
                          <div className="mb-2"><strong>{t('planning_setup.summary_assets')}</strong> {selectedCommunity.community_assets?.length || 0}</div>
                        </>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-md)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                    {formData.analysis_type === 'optimization' && t('planning_setup.review_opt_title')}
                    {formData.analysis_type === 'cba' && t('planning_setup.review_cba_title')}
                    {formData.analysis_type === 'long_term' && t('planning_setup.review_lt_title')}
                  </h6>

                  {/* Day-Ahead Optimization Review */}
                  {formData.analysis_type === 'optimization' && formData.optimizationParams && (
                    <div style={{ fontSize: 'var(--font-size-sm)' }} className="d-flex flex-column gap-2">
                      <div><strong>{t('planning_setup.review_objective')}</strong> {
                        formData.optimizationParams.objective === 'min_cost' ? t('optimization_params.obj_min_cost') :
                        formData.optimizationParams.objective === 'max_self_sufficiency' ? t('optimization_params.obj_max_ss_short') :
                        formData.optimizationParams.objective === 'max_self_consumption' ? t('optimization_params.obj_max_sc_short') :
                        formData.optimizationParams.objective || t('optimization_params.obj_min_cost')
                      }</div>
                      {formData.optimizationParams.loadGrowthPct !== 0 && (
                        <div>&#x2713; <strong>{t('planning_setup.review_load_scaling')}</strong> {formData.optimizationParams.loadGrowthPct > 0 ? '+' : ''}{formData.optimizationParams.loadGrowthPct}%</div>
                      )}
                      {formData.optimizationParams.tariffChangePct !== 0 && (
                        <div>&#x2713; <strong>{t('planning_setup.review_tariff_scaling')}</strong> {formData.optimizationParams.tariffChangePct > 0 ? '+' : ''}{formData.optimizationParams.tariffChangePct}%</div>
                      )}
                      {formData.optimizationParams.addPvSystem && (
                        <div>&#x2713; <strong>{t('planning_setup.review_add_pv')}</strong> {formData.optimizationParams.newPvCapacityKwp} kWp</div>
                      )}
                      {formData.optimizationParams.addWindTurbine && (
                        <div>&#x2713; <strong>{t('planning_setup.review_add_wind')}</strong> {formData.optimizationParams.newWindCapacityKw} kW</div>
                      )}
                      {formData.optimizationParams.addBattery && (
                        <div>&#x2713; <strong>{t('planning_setup.review_add_battery')}</strong> {t('planning_setup.review_battery_specs', {
                          kwh: formData.optimizationParams.newBatteryCapacityKwh,
                          charge: formData.optimizationParams.newBatteryChargeRateKw,
                          discharge: formData.optimizationParams.newBatteryDischargeRateKw,
                          eff: formData.optimizationParams.newBatteryEfficiencyPct,
                        })}</div>
                      )}
                      {!formData.optimizationParams.loadGrowthPct &&
                       !formData.optimizationParams.tariffChangePct &&
                       !formData.optimizationParams.addPvSystem &&
                       !formData.optimizationParams.addWindTurbine &&
                       !formData.optimizationParams.addBattery && (
                        <Alert variant="info" className="mt-2 mb-0">
                          {t('planning_setup.review_no_changes')}
                        </Alert>
                      )}
                    </div>
                  )}

                  {/* CBA Review */}
                  {formData.analysis_type === 'cba' && formData.cbaParams && (() => {
                    const curr = selectedCommunity?.currency || 'CZK'
                    return (
                    <div style={{ fontSize: 'var(--font-size-sm)' }} className="d-flex flex-column gap-2">
                      <div><strong>{t('planning_setup.review_period')}</strong> {t('planning_setup.review_period_years', { count: formData.cbaParams.analysisPeriodYears || 20 })}</div>
                      <div><strong>{t('planning_setup.review_discount')}</strong> {formData.cbaParams.discountRatePct || 5}%</div>
                      <div><strong>{t('planning_setup.review_pv_capex')}</strong> {formData.cbaParams.pvCapexPerKwp || 1000} {curr}/kWp</div>
                      <div><strong>{t('planning_setup.review_batt_capex')}</strong> {formData.cbaParams.battCapexPerKwh || 400} {curr}/kWh</div>
                      <div><strong>{t('planning_setup.review_new_pv')}</strong> {formData.cbaParams.newPvCapacityKwp || 0} kWp</div>
                      <div><strong>{t('planning_setup.review_new_battery')}</strong> {formData.cbaParams.newBatteryCapacityKwh || 0} kWh</div>
                      <div><strong>{t('planning_setup.review_tariff')}</strong> {formData.cbaParams.electricityPurchasePricePerKwh || (curr === 'CZK' ? 5.50 : 0.15)} {curr}/kWh</div>
                      <div><strong>{t('planning_setup.review_co2')}</strong> {formData.cbaParams.gridCO2intensityKgPerKwh || 0.4} kg CO2/kWh</div>
                    </div>
                    )
                  })()}

                  {/* Long-Term Review */}
                  {formData.analysis_type === 'long_term' && formData.longTermParams && (
                    <div style={{ fontSize: 'var(--font-size-sm)' }} className="d-flex flex-column gap-2">
                      <div><strong>{t('planning_setup.review_time_horizon')}</strong> {
                        formData.longTermParams.timeHorizon === '1_year' ? t('long_term_params.horizon_1y') :
                        formData.longTermParams.timeHorizon === '5_years' ? t('long_term_params.horizon_5y') :
                        formData.longTermParams.timeHorizon === '20_years' ? t('long_term_params.horizon_20y') : t('long_term_params.horizon_10y')
                      }</div>
                      {formData.longTermParams.loadGrowthPct !== 0 && (
                        <div>&#x2713; <strong>{t('planning_setup.review_lt_load_growth')}</strong> {formData.longTermParams.loadGrowthPct > 0 ? '+' : ''}{formData.longTermParams.loadGrowthPct}%</div>
                      )}
                      {formData.longTermParams.tariffChangePct !== 0 && (
                        <div>&#x2713; <strong>{t('planning_setup.review_lt_tariff_esc')}</strong> {formData.longTermParams.tariffChangePct > 0 ? '+' : ''}{formData.longTermParams.tariffChangePct}%</div>
                      )}
                      {formData.longTermParams.pvAnnualDegradationPct != null && formData.longTermParams.pvAnnualDegradationPct !== 0.5 && (
                        <div>&#x2713; <strong>{t('planning_setup.review_lt_pv_deg')}</strong> {formData.longTermParams.pvAnnualDegradationPct}%/yr</div>
                      )}
                      {formData.longTermParams.addPvSystem && (
                        <div>&#x2713; <strong>{t('planning_setup.review_add_pv')}</strong> {formData.longTermParams.newPvCapacityKwp} kWp</div>
                      )}
                      {formData.longTermParams.addWindTurbine && (
                        <div>&#x2713; <strong>{t('planning_setup.review_add_wind')}</strong> {formData.longTermParams.newWindCapacityKw} kW</div>
                      )}
                      {formData.longTermParams.addBattery && (
                        <div>&#x2713; <strong>{t('planning_setup.review_add_battery')}</strong> {formData.longTermParams.newBatteryCapacityKwh} kWh</div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Error Display */}
          {(createScenario.isError || updateScenario.isError) && (
            <Alert variant="danger" className="mt-3">
              {createScenario.error?.message || updateScenario.error?.message || t('planning_setup.save_failed')}
            </Alert>
          )}

          {/* Navigation Buttons */}
          <div className="d-flex justify-content-between mt-5">
            <div>
              {step > 1 && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setStep(step - 1)}
                >
                  {t('planning_setup.btn_back')}
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!validateStep()}
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    borderColor: 'var(--color-primary)',
                    fontWeight: 600,
                  }}
                >
                  {t('planning_setup.btn_next')}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!validateStep() || createScenario.isPending || updateScenario.isPending}
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    borderColor: 'var(--color-accent)',
                    fontWeight: 600,
                    padding: 'var(--space-3) var(--space-8)',
                  }}
                >
                  {(createScenario.isPending || updateScenario.isPending) ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('planning_setup.btn_saving')}
                    </>
                  ) : (
                    isEditing ? t('planning_setup.btn_update') : t('planning_setup.btn_create')
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default PlanningSetupPage
